import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import moment from "moment";
import { http } from "../../../http";
import { API_ROUTES } from "../../../config";
import { listPeopleService } from "../../../services/people";
import {
  getFeedsService,
  updateFeedService,
  deleteFeedService,
} from "../../../services/feeds";
import { getFeedTypeColor } from "../../../utils/feedUtils";

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MegaphoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

// ─── Main Component ─────────────────────────────────────────────────────────

const MessageCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getHttpsValidationMessage = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return "";
    return trimmedValue.startsWith("https://") ? "" : "https is required";
  };

  // Tab state
  const [activeTab, setActiveTab] = useState("compose");

  // Compose form state
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [linkError, setLinkError] = useState("");
  const [feedType, setFeedType] = useState("ANNOUNCEMENT");
  const [recipientType, setRecipientType] = useState("broadcast");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Sent messages state
  const [sentMessages, setSentMessages] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [sentPage, setSentPage] = useState(1);
  const [hasMoreSent, setHasMoreSent] = useState(true);

  // Edit feed modal state
  const [editingFeed, setEditingFeed] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    message: "",
    description: "",
    link: "",
    feed_type: "ANNOUNCEMENT",
  });
  const [editLinkError, setEditLinkError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete confirmation state
  const [deletingFeedId, setDeletingFeedId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listPeopleService({ limit: 1000, currentPage: 0 });
      const usersArray = response?.data?.data || response?.data || [];

      const options = usersArray.map((u) => ({
        value: u._id || u.id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
      }));
      setAllUsers(options);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSentMessages = useCallback(async (page = 1, append = false) => {
    setLoadingSent(true);
    try {
      const response = await getFeedsService(page, 20, "sent");
      const items =
        response?.data?.feeds || response?.data || response?.feeds || [];
      const feedItems = Array.isArray(items) ? items : [];

      if (append) {
        setSentMessages((prev) => [...prev, ...feedItems]);
      } else {
        setSentMessages(feedItems);
      }
      setHasMoreSent(feedItems.length >= 20);
    } catch (err) {
      console.error("Error fetching sent messages:", err);
      toast.error("Failed to load sent messages");
    } finally {
      setLoadingSent(false);
    }
  }, []);

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setDescription("");
    setLink("");
    setLinkError("");
    setFeedType("ANNOUNCEMENT");
  };

  useEffect(() => {
    // If navigated from People module with a pre-selected user
    if (location.state?.selectedUser) {
      setRecipientType("selected");
      const user = location.state.selectedUser;
      setSelectedUsers([
        {
          value: user._id || user.id,
          label:
            `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
            user.email,
        },
      ]);
    }

    // Fetch all members for the dropdown
    fetchUsers();
  }, [location, fetchUsers]);

  // Fetch sent messages when tab switches to "sent"
  useEffect(() => {
    if (activeTab === "sent") {
      setSentPage(1);
      fetchSentMessages(1, false);
    }
  }, [activeTab, fetchSentMessages]);

  // Custom Option component with Checkbox
  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={
              props.isSelected ||
              (props.data.value === "selectAll" &&
                selectedUsers.length === allUsers.length &&
                allUsers.length > 0)
            }
            onChange={() => null}
            className="mr-2 w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
          />
          <label className="text-sm cursor-pointer">{props.label}</label>
        </div>
      </components.Option>
    );
  };

  const handleSelectChange = (selected) => {
    if (selected && selected.length > 0) {
      const lastItem = selected[selected.length - 1];
      if (lastItem && lastItem.value === "selectAll") {
        if (selectedUsers.length === allUsers.length) {
          return setSelectedUsers([]);
        }
        return setSelectedUsers(allUsers);
      }
    }

    setSelectedUsers(
      selected ? selected.filter((s) => s.value !== "selectAll") : [],
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    if (!message.trim()) return toast.error("Message is required");

    const nextLinkError = getHttpsValidationMessage(link);
    if (nextLinkError) {
      setLinkError(nextLinkError);
      return;
    }

    if (recipientType === "selected" && selectedUsers.length === 0) {
      return toast.error("Please select at least one recipient");
    }

    setIsSending(true);
    try {
      let type = "ALL";
      let user_ids = undefined;

      if (recipientType === "selected") {
        if (selectedUsers.length === 1) {
          type = "USER";
        } else if (selectedUsers.length > 1) {
          type = "MULTIPLE";
        }
        user_ids = selectedUsers.map((u) => u.value);
      }

      const payload = {
        recipient_type: type,
        title: title.trim(),
        message: message.trim(),
        description: description.trim(),
        feed_type: feedType,
      };

      if (user_ids) {
        payload.user_ids = user_ids;
      }

      if (link.trim()) {
        payload.link = link.trim();
      }

      await http().post(API_ROUTES.notifications + "/broadcast", payload);

      toast.success("Message sent successfully");
      // Reset form
      resetForm();
      if (!location.state?.selectedUser) {
        setRecipientType("broadcast");
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleEditResend = (feed) => {
    setTitle(feed.title || "");
    setMessage(feed.message || "");
    setDescription(feed.description || "");
    setLink(feed.link || "");
    setFeedType(feed.feed_type || "ANNOUNCEMENT");
    setRecipientType("broadcast");
    setSelectedUsers([]);
    setActiveTab("compose");
    toast.info("Message loaded for editing. Make your changes and resend.");
  };

  const handleEditFeed = (feed) => {
    setEditingFeed(feed);
    setEditForm({
      title: feed.title || "",
      message: feed.message || "",
      description: feed.description || "",
      link: feed.link || "",
      feed_type: feed.feed_type || "ANNOUNCEMENT",
    });
    setEditLinkError("");
  };

  const handleUpdateFeed = async () => {
    if (!editForm.title.trim()) return toast.error("Title is required");
    if (!editForm.message.trim()) return toast.error("Message is required");

    const nextEditLinkError = getHttpsValidationMessage(editForm.link);
    if (nextEditLinkError) {
      setEditLinkError(nextEditLinkError);
      return;
    }

    setIsUpdating(true);
    try {
      const feedId = editingFeed._id || editingFeed.id;
      const payload = {
        title: editForm.title.trim(),
        message: editForm.message.trim(),
        description: editForm.description.trim(),
        link: editForm.link.trim(),
        feed_type: editForm.feed_type,
        type: editForm.feed_type,
      };
      await updateFeedService(feedId, payload);
      toast.success("Feed updated successfully");
      setEditingFeed(null);
      setSentPage(1);
      fetchSentMessages(1, false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update feed");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteFeed = async () => {
    if (!deletingFeedId) return;
    setIsDeleting(true);
    try {
      await deleteFeedService(deletingFeedId);
      toast.success("Feed deleted successfully");
      setSentMessages((prev) =>
        prev.filter((f) => (f._id || f.id) !== deletingFeedId),
      );
      setDeletingFeedId(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete feed");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = sentPage + 1;
    setSentPage(nextPage);
    fetchSentMessages(nextPage, true);
  };

  const getVisibilityBadge = (feed) => {
    const visibility = feed?.visibility_type?.toUpperCase();
    if (visibility === "PUBLIC") {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
          <GlobeIcon /> Public
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-100">
        <UserIcon /> Private
      </span>
    );
  };

  const getRecipientInfo = (feed) => {
    const visibility = feed?.visibility_type?.toUpperCase();
    if (visibility === "PUBLIC") {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <UsersIcon /> All Users
        </span>
      );
    }
    const userCount = feed?.visible_user_ids?.length || 0;
    if (userCount > 0) {
      const tooltip = feed.visible_user_ids
        .map(
          (id) => allUsers.find((u) => u.value === id)?.label || "Unknown User",
        )
        .join(", ");
      return (
        <span
          className="inline-flex items-center gap-1.5 text-xs text-slate-500"
          title={tooltip}
        >
          <UserIcon /> {userCount}{" "}
          {userCount === 1 ? "recipient" : "recipients"}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <UserIcon /> Specific users
      </span>
    );
  };

  // ─── Compose Form ────────────────────────────────────────────────────────

  const renderComposeForm = () => (
    <div className="bg-white rounded-lg border p-6 shadow-sm">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <p className="text-xs text-slate-500">
          Fields marked with{" "}
          <span className="text-rose-500 font-semibold">*</span> are required.
        </p>
        {/* Recipient Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient Type <span className="text-rose-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recipientType"
                value="broadcast"
                checked={recipientType === "broadcast"}
                onChange={() => setRecipientType("broadcast")}
                className="w-4 h-4 text-green-600"
              />
              Broadcast (All Users)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="recipientType"
                value="selected"
                checked={recipientType === "selected"}
                onChange={() => setRecipientType("selected")}
                className="w-4 h-4 text-green-600"
              />
              Selected Users
            </label>
          </div>
        </div>

        {recipientType === "selected" && (
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Users
            </label>
            <Select
              isMulti
              options={[
                { value: "selectAll", label: "Select All Users" },
                ...allUsers,
              ]}
              value={selectedUsers}
              onChange={handleSelectChange}
              isLoading={isLoading}
              placeholder="Search and select users..."
              className="basic-multi-select text-sm"
              classNamePrefix="select"
              components={{ Option: CustomOption }}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              onKeyDown={(e) => {
                if (e.key === " " && !e.target.value) {
                  e.preventDefault();
                }
              }}
            />
          </div>
        )}

        {/* Feed Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Feed Type <span className="text-rose-500">*</span>
          </label>
          <select
            value={feedType}
            onChange={(e) => setFeedType(e.target.value)}
            className="w-fit border rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 bg-white"
          >
            <option value="ANNOUNCEMENT">Announcement</option>
            <option value="CELEBRATION">Celebration</option>
            <option value="APPRECIATION">Appreciation</option>
            <option value="IMPORTANT">Important</option>
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title / Subject <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600"
            placeholder="Enter message title..."
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message <span className="text-rose-500">*</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 resize-y"
            placeholder="Write the message here..."
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 resize-y"
            placeholder="Write the description here..."
          />
        </div>

        {/* Link */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Link
          </label>
          <input
            type="url"
            value={link}
            onChange={(e) => {
              const nextValue = e.target.value;
              setLink(nextValue);
              setLinkError(getHttpsValidationMessage(nextValue));
            }}
            className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-1 ${
              linkError
                ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500"
                : "focus:border-green-600 focus:ring-green-600"
            }`}
            placeholder="https://example.com"
          />
          {linkError ? (
            <p className="mt-2 text-sm text-rose-600">{linkError}</p>
          ) : null}
        </div>

        {/* Submit */}
        <div className="pt-4 border-t flex justify-end">
          <button
            type="submit"
            disabled={isSending}
            className={`px-8 py-2 text-sm bg-[#057D54] text-white rounded-lg flex items-center gap-2 font-medium ${
              isSending ? "opacity-70 cursor-not-allowed" : "hover:bg-green-600"
            }`}
          >
            {isSending ? (
              <>Sending...</>
            ) : (
              <>
                <SendIcon />
                Send Message
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // ─── Sent Messages List ──────────────────────────────────────────────────

  const renderSentMessages = () => (
    <div className="space-y-4">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {sentMessages.length} message{sentMessages.length !== 1 ? "s" : ""}{" "}
          found
        </p>
        <button
          onClick={() => {
            setSentPage(1);
            fetchSentMessages(1, false);
          }}
          disabled={loadingSent}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {loadingSent && sentMessages.length === 0 ? (
        // Loading skeleton
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm animate-pulse"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-16 bg-slate-200 rounded-full" />
                    <div className="h-5 w-24 bg-slate-100 rounded-full" />
                  </div>
                  <div className="h-5 w-3/4 bg-slate-200 rounded" />
                  <div className="h-4 w-full bg-slate-100 rounded" />
                  <div className="h-4 w-5/6 bg-slate-100 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : sentMessages.length === 0 ? (
        // Empty state
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4 text-slate-300">
            <MegaphoneIcon />
          </div>
          <h3 className="text-base font-bold text-slate-700">
            No messages sent yet
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Compose a new message to get started.
          </p>
          <button
            onClick={() => setActiveTab("compose")}
            className="mt-4 px-5 py-2 bg-[#057D54] text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
          >
            Compose Message
          </button>
        </div>
      ) : (
        <>
          {sentMessages.map((feed) => {
            const isPublic = feed?.visibility_type?.toUpperCase() === "PUBLIC";
            const feedId = feed._id || feed.id;
            const author = feed?.created_by || feed?.createdBy || {};
            const authorName =
              `${author?.first_name || ""} ${author?.last_name || ""}`.trim() ||
              "Admin";
            const authorInitial = author?.first_name?.[0]?.toUpperCase() || "A";

            return (
              <article
                key={feedId}
                onClick={() => navigate(`/feeds/${feedId}`)}
                className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
              >
                {/* Card Header */}
                <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold uppercase shrink-0">
                      {authorInitial}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {authorName}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-400">
                          {moment(feed.createdAt).format("MMM DD, YYYY")} at{" "}
                          {moment(feed.createdAt).format("hh:mm A")}
                        </p>
                        {feed?.is_edited && (
                          <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-amber-700 bg-amber-50 border border-amber-100">
                            Edited
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {getVisibilityBadge(feed)}
                    <span
                      className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getFeedTypeColor(feed.feed_type || feed.type || "ANNOUNCEMENT")}`}
                    >
                      {feed.feed_type || feed.type || "ANNOUNCEMENT"}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="px-5 pb-4 mt-3">
                  <h4 className="text-base font-bold text-slate-900 mb-1.5">
                    {feed.title || "Untitled"}
                  </h4>
                  {feed.message && (
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap line-clamp-3 mb-1">
                      {feed.message}
                    </p>
                  )}
                  {feed.description && (
                    <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-wrap line-clamp-2">
                      {feed.description}
                    </p>
                  )}
                  {feed.link && (
                    <a
                      href={feed.link}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-700 break-all underline decoration-blue-200"
                    >
                      {feed.link}
                    </a>
                  )}
                </div>

                {/* Card Footer */}
                <div className="px-5 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {getRecipientInfo(feed)}
                    <span className="text-xs text-slate-400">
                      {moment(feed.createdAt).fromNow()}
                    </span>
                  </div>

                  {isPublic && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFeed(feed);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#057D54] hover:text-green-700 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors border border-green-100"
                      >
                        <EditIcon />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingFeedId(feedId);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-100"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}

          {/* Load More */}
          {hasMoreSent && (
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={loadingSent}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 px-6 py-2.5 rounded-lg transition-colors shadow-sm disabled:opacity-50"
              >
                {loadingSent ? (
                  <>
                    <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="w-full h-fit flex flex-col gap-4 px-4 container mx-auto mb-10">
      {/* Page Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm mt-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Messages</h2>
          <p className="text-sm text-gray-500">
            {activeTab === "compose"
              ? "Create and send a new message"
              : "View all sent messages"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab("compose")}
            className={`flex-1 px-6 py-3.5 text-sm font-semibold transition-all relative ${
              activeTab === "compose"
                ? "text-[#057D54] bg-green-50/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <SendIcon />
              Compose
            </span>
            {activeTab === "compose" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#057D54]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("sent")}
            className={`flex-1 px-6 py-3.5 text-sm font-semibold transition-all relative ${
              activeTab === "sent"
                ? "text-[#057D54] bg-green-50/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <MegaphoneIcon />
              Sent Messages
            </span>
            {activeTab === "sent" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#057D54]" />
            )}
          </button>
        </div>

        <div className="p-5">
          {activeTab === "compose" ? renderComposeForm() : renderSentMessages()}
        </div>
      </div>

      {/* ── Edit Feed Modal ───────────────────────────────────────────── */}
      {editingFeed && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Edit Feed</h3>
              <button
                onClick={() => !isUpdating && setEditingFeed(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Feed Type
                </label>
                <select
                  value={editForm.feed_type}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, feed_type: e.target.value }))
                  }
                  className="w-full border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 bg-white"
                >
                  <option value="ANNOUNCEMENT">Announcement</option>
                  <option value="CELEBRATION">Celebration</option>
                  <option value="APPRECIATION">Appreciation</option>
                  <option value="IMPORTANT">Important</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="w-full border border-slate-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600"
                  placeholder="Feed title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message
                </label>
                <textarea
                  value={editForm.message}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, message: e.target.value }))
                  }
                  rows={3}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 resize-y"
                  placeholder="Feed message..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 resize-y"
                  placeholder="Feed description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Link
                </label>
                <input
                  type="url"
                  value={editForm.link}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setEditForm((p) => ({ ...p, link: nextValue }));
                    setEditLinkError(getHttpsValidationMessage(nextValue));
                  }}
                  className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-1 ${
                    editLinkError
                      ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500"
                      : "border-slate-300 focus:border-green-600 focus:ring-green-600"
                  }`}
                  placeholder="https://example.com"
                />
                {editLinkError ? (
                  <p className="mt-2 text-sm text-rose-600">
                    {editLinkError}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setEditingFeed(null)}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFeed}
                disabled={isUpdating}
                className={`px-6 py-2 text-sm font-medium text-white bg-[#057D54] rounded-lg flex items-center gap-2 transition-colors ${isUpdating ? "opacity-70 cursor-not-allowed" : "hover:bg-green-600"}`}
              >
                {isUpdating ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{" "}
                    Updating...
                  </>
                ) : (
                  <>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Update Feed
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────── */}
      {deletingFeedId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !isDeleting && setDeletingFeedId(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                Delete Feed
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Are you sure you want to delete this feed? This action cannot be
                undone.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setDeletingFeedId(null)}
                  disabled={isDeleting}
                  className="px-5 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFeed}
                  disabled={isDeleting}
                  className={`px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg flex items-center gap-2 transition-colors ${isDeleting ? "opacity-70 cursor-not-allowed" : "hover:bg-red-700"}`}
                >
                  {isDeleting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{" "}
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageCreate;
